import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../shared/ui/Button.js';
import { Dialog } from '../../shared/ui/Dialog.js';
import { useOptionalOverlayBackDismiss } from '../../shared/context/OverlayBackDismissContext.js';

interface PromoExclusiveConflictModalProps {
  readonly isOpen: boolean;
  readonly onChoosePromo: () => void;
  readonly onChooseLoyalty: () => void | Promise<void>;
  readonly onClose: () => void;
  readonly isChoosingLoyalty?: boolean;
  readonly chooseLoyaltyErrorKey?: string | null;
}

export function PromoExclusiveConflictModal({
  isOpen,
  onChoosePromo,
  onChooseLoyalty,
  onClose,
  isChoosingLoyalty = false,
  chooseLoyaltyErrorKey = null,
}: PromoExclusiveConflictModalProps): JSX.Element | null {
  const { t } = useTranslation('customer');
  const overlayBack = useOptionalOverlayBackDismiss();
  const setOverlayDismiss = overlayBack?.setOverlayDismiss;

  useEffect(() => {
    if (!isOpen || setOverlayDismiss === undefined) {
      return;
    }
    setOverlayDismiss(isChoosingLoyalty ? null : onClose);
    return (): void => {
      setOverlayDismiss(null);
    };
  }, [isOpen, isChoosingLoyalty, onClose, setOverlayDismiss]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      title={t('promo.exclusive.title')}
      titleId="customer-promo-exclusive-title"
      pending={isChoosingLoyalty}
      closeOnOverlayClick
      closeLabel={t('promo.exclusive.closeBackdrop')}
      testId="customer-promo-exclusive-modal"
      className="max-w-md"
      hideHeader
    >
      <h2
        className="m-0 text-lg font-semibold text-[var(--color-on-surface)]"
        aria-hidden="true"
      >
        {t('promo.exclusive.title')}
      </h2>
      <p className="mt-4 mb-6 text-sm leading-normal text-[var(--color-on-surface-muted)]">
        {t('promo.exclusive.chooseOne')}
      </p>
      {chooseLoyaltyErrorKey != null ? (
        <p
          className="mb-4 text-sm text-[var(--color-danger)]"
          role="alert"
          data-testid="customer-promo-exclusive-loyalty-error"
        >
          {t(chooseLoyaltyErrorKey)}
        </p>
      ) : null}
      <div className="flex flex-col justify-end gap-3 sm:flex-row">
        <Button
          intent="primary"
          size="md"
          type="button"
          disabled={isChoosingLoyalty}
          onClick={onChoosePromo}
        >
          {t('promo.exclusive.usePromo')}
        </Button>
        <Button
          intent="secondary"
          size="md"
          type="button"
          disabled={isChoosingLoyalty}
          data-testid="customer-promo-exclusive-use-loyalty"
          onClick={() => {
            void onChooseLoyalty();
          }}
        >
          {isChoosingLoyalty
            ? t('promo.exclusive.releasing')
            : t('promo.exclusive.useLoyalty')}
        </Button>
      </div>
    </Dialog>
  );
}
